import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import type { AppDispatch, RootState } from '../store';
import { RequestStatus } from '../utils';
import { SCHOOL_DIRECTORY_DEV_PREVIEW } from './schoolDirectory';

/**
 * Platform-admin side of the school directory (PTEC-577): cross-state search
 * (enriched records — the server projects by group) and the ingestion service
 * (jobs, history, manual runs). Admin-only surfaces consume this; the consumer
 * picker keeps its own lean slice.
 */

/**
 * Canonical control (public/private) enum, captured at ingestion across all
 * types: CCD K-12 is always 'public' (it is the public-school universe;
 * charters are public); IPEDS higher-ed maps inst_control 1/2/3.
 */
export type SchoolControl = 'public' | 'privateNonprofit' | 'privateForProfit';

export interface AdminDirectorySchool {
	name: string;
	city?: string;
	state?: string;
	type?: string;
	control?: SchoolControl;
	phone?: string;
	street?: string;
	zip?: string;
	domain?: string;
	sourceId?: string;
}

export interface IngestionJob {
	jobId: string;
	status: 'queued' | 'running' | 'completed' | 'failed';
	startedAt: string;
	finishedAt?: string;
	startedBy?: string;
	year?: number;
	statesTotal?: number;
	statesDone?: number;
	schoolsWritten?: number;
	error?: string;
	/** Last sign of life. A healthy run stamps this after every subdivision. */
	updatedAt?: string;
	/** Times the watchdog has resurrected this run without it progressing. */
	revivals?: number;
	/**
	 * Decided server-side [PTEC-581]: the staleness threshold belongs with the
	 * watchdog that acts on it, so the client renders this rather than
	 * recomputing it from `updatedAt` against a duplicated constant.
	 */
	stalled?: boolean;
}

/** 'auto' = resolve the latest published vintage at each scheduled run. */
export type IngestYear = number | 'auto';

export interface IngestConfig {
	enabled: boolean;
	intervalDays: number;
	year: IngestYear;
}

interface SchoolDirectoryAdminState {
	search: { query: string; results: AdminDirectorySchool[]; status: RequestStatus };
	jobs: { items: IngestionJob[]; status: RequestStatus; available: boolean };
	startJob: { status: RequestStatus; lastJobId?: string };
	config: { value: IngestConfig | null; status: RequestStatus; saveStatus: RequestStatus };
	/** Vintages both sources have published (server-probed) — feeds the year picker. */
	vintages: { years: number[]; status: RequestStatus };
}

const initialState: SchoolDirectoryAdminState = {
	search: { query: '', results: [], status: RequestStatus.IDLE },
	jobs: { items: [], status: RequestStatus.IDLE, available: true },
	startJob: { status: RequestStatus.IDLE },
	config: { value: null, status: RequestStatus.IDLE, saveStatus: RequestStatus.IDLE },
	vintages: { years: [], status: RequestStatus.IDLE }
};

/** Dev preview: search the local fixture so the grid is playable pre-deploy. */
const searchFixture = async (query: string): Promise<AdminDirectorySchool[] | null> => {
	try {
		const response = await fetch('/fixtures/schoolDirectory-US.json');
		if (!response.ok) {
			return null;
		}
		const fixture = await response.json();
		const needle = query.toLowerCase();
		const results: AdminDirectorySchool[] = [];
		for (const [scope, schools] of Object.entries(fixture.scopes as Record<string, AdminDirectorySchool[]>)) {
			const [state, type] = scope.split('#');
			for (const school of schools) {
				if (school.name.toLowerCase().includes(needle)) {
					results.push({ ...school, state, type });
					if (results.length >= 50) {
						return results;
					}
				}
			}
		}
		return results;
	} catch {
		return null;
	}
};

export const searchDirectory = createAsyncThunk<
	{ query: string; results: AdminDirectorySchool[] },
	{ country: string; query: string },
	{ dispatch: AppDispatch; state: RootState }
>('schoolDirectoryAdmin/search', async ({ country, query }) => {
	if (SCHOOL_DIRECTORY_DEV_PREVIEW) {
		const fixtureResults = await searchFixture(query);
		if (fixtureResults) {
			return { query, results: fixtureResults };
		}
	}
	const response = await API.get(
		'inspirusapi',
		`/school-directory/${encodeURIComponent(country)}/search?q=${encodeURIComponent(query)}`,
		{}
	);
	return { query, results: Array.isArray(response?.schools) ? response.schools : [] };
});

export const fetchIngestionJobs = createAsyncThunk<IngestionJob[], void, { dispatch: AppDispatch; state: RootState }>(
	'schoolDirectoryAdmin/fetchJobs',
	async () => {
		const response = await API.get('inspirusapi', '/school-directory-admin/jobs', {});
		return Array.isArray(response?.jobs) ? response.jobs : [];
	}
);

export const fetchIngestConfig = createAsyncThunk<IngestConfig, void, { dispatch: AppDispatch; state: RootState }>(
	'schoolDirectoryAdmin/fetchConfig',
	async () => {
		const response = await API.get('inspirusapi', '/school-directory-admin/config', {});
		return {
			enabled: response?.enabled === true,
			intervalDays: response?.intervalDays ?? 1,
			year: response?.year ?? 'auto'
		};
	}
);

export const fetchVintages = createAsyncThunk<number[], void, { dispatch: AppDispatch; state: RootState }>(
	'schoolDirectoryAdmin/fetchVintages',
	async () => {
		const response = await API.get('inspirusapi', '/school-directory-admin/vintages', {});
		return Array.isArray(response?.years) ? response.years : [];
	}
);

export const saveIngestConfig = createAsyncThunk<
	IngestConfig,
	IngestConfig,
	{ dispatch: AppDispatch; state: RootState }
>('schoolDirectoryAdmin/saveConfig', async (config) => {
	await API.put('inspirusapi', '/school-directory-admin/config', { body: config });
	return config;
});

export const startIngestion = createAsyncThunk<
	{ jobId: string },
	{ year: IngestYear; states?: string[] },
	{ dispatch: AppDispatch; state: RootState }
>('schoolDirectoryAdmin/startIngestion', async (payload) => {
	const response = await API.post('inspirusapi', '/school-directory-admin/ingest', { body: payload });
	return { jobId: response?.jobId ?? '' };
});

const schoolDirectoryAdminSlice = createSlice({
	name: 'schoolDirectoryAdmin',
	initialState,
	reducers: {
		clearDirectorySearch: (state) => {
			state.search = initialState.search;
		}
	},
	extraReducers: (builder) => {
		builder.addCase(searchDirectory.pending, (state, action) => {
			state.search.query = action.meta.arg.query;
			state.search.status = RequestStatus.PENDING;
		});
		builder.addCase(searchDirectory.fulfilled, (state, action) => {
			// Ignore stale responses from superseded queries.
			if (action.payload.query === state.search.query) {
				state.search.results = action.payload.results;
				state.search.status = RequestStatus.SUCCESS;
			}
		});
		builder.addCase(searchDirectory.rejected, (state, action) => {
			if (action.meta.arg.query === state.search.query) {
				state.search.results = [];
				state.search.status = RequestStatus.ERROR;
			}
		});
		builder.addCase(fetchIngestionJobs.pending, (state) => {
			state.jobs.status = RequestStatus.PENDING;
		});
		builder.addCase(fetchIngestionJobs.fulfilled, (state, action) => {
			state.jobs.items = action.payload;
			state.jobs.status = RequestStatus.SUCCESS;
			state.jobs.available = true;
		});
		builder.addCase(fetchIngestionJobs.rejected, (state) => {
			// Endpoint absent (service not deployed here yet) → the tab shows
			// its unavailable state rather than an error wall.
			state.jobs.status = RequestStatus.ERROR;
			state.jobs.available = false;
		});
		builder.addCase(fetchIngestConfig.pending, (state) => {
			state.config.status = RequestStatus.PENDING;
		});
		builder.addCase(fetchIngestConfig.fulfilled, (state, action) => {
			state.config.value = action.payload;
			state.config.status = RequestStatus.SUCCESS;
		});
		builder.addCase(fetchIngestConfig.rejected, (state) => {
			state.config.status = RequestStatus.ERROR;
		});
		builder.addCase(fetchVintages.pending, (state) => {
			state.vintages.status = RequestStatus.PENDING;
		});
		builder.addCase(fetchVintages.fulfilled, (state, action) => {
			state.vintages.years = action.payload;
			state.vintages.status = RequestStatus.SUCCESS;
		});
		builder.addCase(fetchVintages.rejected, (state) => {
			state.vintages.status = RequestStatus.ERROR;
		});
		builder.addCase(saveIngestConfig.pending, (state) => {
			state.config.saveStatus = RequestStatus.PENDING;
		});
		builder.addCase(saveIngestConfig.fulfilled, (state, action) => {
			state.config.value = action.payload;
			state.config.saveStatus = RequestStatus.SUCCESS;
		});
		builder.addCase(saveIngestConfig.rejected, (state) => {
			state.config.saveStatus = RequestStatus.ERROR;
		});
		builder.addCase(startIngestion.pending, (state) => {
			state.startJob.status = RequestStatus.PENDING;
		});
		builder.addCase(startIngestion.fulfilled, (state, action) => {
			state.startJob.status = RequestStatus.SUCCESS;
			state.startJob.lastJobId = action.payload.jobId;
		});
		builder.addCase(startIngestion.rejected, (state) => {
			state.startJob.status = RequestStatus.ERROR;
		});
	}
});

export const { clearDirectorySearch } = schoolDirectoryAdminSlice.actions;
export default schoolDirectoryAdminSlice.reducer;
